/**
 * MDExam Legacy → Core Migration Audit (Super Admin)
 *
 * TEMPORARY MIGRATION TOOLING - TO BE REMOVED AFTER CUTOVER
 *
 * Tracks how completely the legacy Rails mdexam database has been migrated into
 * core. Counts and config diffs load live; the id-level reconciliation is
 * expensive (it scans every key on both clusters) so it runs on demand per row.
 */

import { useState, useEffect, useCallback } from 'react'
import { adminAPI } from '../services/api'
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
  Button
} from '../components/ui'
import {
  RefreshCw, Loader2, AlertCircle, Check, X, Info,
  ChevronDown, ChevronRight, Play, Database
} from 'lucide-react'
import OrderStatusInfo from '../components/OrderStatusInfo'

const TABS = [
  { value: 'overview', label: 'Overview' },
  { value: 'programs', label: 'Programs' },
  { value: 'pharmacies', label: 'Pharmacies' },
  { value: 'physicians', label: 'Physicians' },
  { value: 'queues', label: 'Doctor Queues' },
  { value: 'routing', label: 'Routing' },
]

const CONFIG_TABS = new Set(['programs', 'pharmacies', 'physicians'])

/**
 * MDExam splits into two core clinics. Every tab except Overview reconciles
 * against exactly one of them, so those tabs carry a target selector; Overview
 * shows both side by side.
 */
const TARGETS = [
  { id: 'rrv', label: 'MDExam RRV' },
  { id: 'wl', label: 'MDExam WL' },
]
const TARGET_SCOPED = (tab) => tab !== 'overview' && tab !== 'routing'

const num = (n) => (n === null || n === undefined ? '—' : n.toLocaleString())

const signed = (n) => {
  if (n === null || n === undefined) return '—'
  return n > 0 ? `+${n.toLocaleString()}` : n.toLocaleString()
}

const cents = (c) => {
  if (c === null || c === undefined) return '—'
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', maximumFractionDigits: 0,
  }).format(c / 100)
}

/** Render a normalized field value compactly, with arrays truncated. */
function fieldValue(v) {
  if (v === null || v === undefined) return <span className="text-muted-foreground italic">not set</span>
  if (Array.isArray(v)) {
    if (v.length === 0) return <span className="text-muted-foreground italic">empty</span>
    const shown = v.slice(0, 12).join(', ')
    return <span>[{shown}{v.length > 12 ? `, …+${v.length - 12}` : ''}]</span>
  }
  if (typeof v === 'object') return <span className="font-mono text-xs">{JSON.stringify(v)}</span>
  if (typeof v === 'boolean') return <span>{String(v)}</span>
  return <span>{String(v)}</span>
}

function StatusPill({ inSync, label }) {
  if (inSync === null || inSync === undefined) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-500/10 text-blue-600">
        <Info className="h-3 w-3" />{label || 'Informational'}
      </span>
    )
  }
  return inSync ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-500/10 text-green-600">
      <Check className="h-3 w-3" />{label || 'In sync'}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-500/10 text-red-600">
      <X className="h-3 w-3" />{label || 'Mismatch'}
    </span>
  )
}

/** One row of the overview table, with optional on-demand id-level drill-down. */
function CountRow({ row, idResult, running, onRunIdDiff }) {
  const [open, setOpen] = useState(false)
  const mismatch = row.in_sync === false

  return (
    <div className="border rounded-lg">
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium">{row.label}</span>
            <StatusPill inSync={row.in_sync} />
          </div>
          {row.note && (
            <p className="text-xs text-muted-foreground mt-1">{row.note}</p>
          )}
        </div>

        <div className="text-right shrink-0 w-28">
          <div className="text-xs text-muted-foreground">Legacy</div>
          <div className="font-mono text-sm">{num(row.legacy_count)}</div>
        </div>
        <div className="text-right shrink-0 w-28">
          <div className="text-xs text-muted-foreground">Core</div>
          <div className="font-mono text-sm">{num(row.core_count)}</div>
        </div>
        <div className="text-right shrink-0 w-24">
          <div className="text-xs text-muted-foreground">Delta</div>
          <div className={`font-mono text-sm ${mismatch ? 'text-red-500' : 'text-muted-foreground'}`}>
            {signed(row.delta)}
          </div>
        </div>

        {row.drillable && (
          <Button
            variant="outline" size="sm" className="h-7 text-xs shrink-0"
            disabled={running}
            onClick={() => { setOpen(true); onRunIdDiff(row.kind) }}
          >
            {running
              ? <><Loader2 className="h-3 w-3 animate-spin mr-1" />Scanning…</>
              : <><Play className="h-3 w-3 mr-1" />ID diff</>}
          </Button>
        )}
      </div>

      {/* Payments carries extra reconciliation context worth showing inline */}
      {(row.core_migrated !== undefined || row.legacy_amount_cents !== undefined) && (
        <div className="px-4 pb-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
          <div>
            <div className="text-muted-foreground">Core migrated</div>
            <div className="font-mono">{num(row.core_migrated)}</div>
          </div>
          <div>
            <div className="text-muted-foreground">Core native (post-cutover)</div>
            <div className="font-mono">{num(row.core_native)}</div>
          </div>
          <div>
            <div className="text-muted-foreground">Legacy total</div>
            <div className="font-mono">{cents(row.legacy_amount_cents)}</div>
          </div>
          <div>
            <div className="text-muted-foreground">Core total</div>
            <div className="font-mono">
              {cents(row.core_amount_cents)}
              {row.amount_delta_cents !== 0 && (
                <span className="text-red-500 ml-1">({cents(row.amount_delta_cents)})</span>
              )}
            </div>
          </div>
          {row.legacy_excluded_dummy > 0 && (
            <div className="col-span-2 sm:col-span-4 text-muted-foreground">
              Excludes {num(row.legacy_excluded_dummy)} legacy <code>dummy:"true"</code> placeholder rows —
              never migration candidates.
            </div>
          )}
        </div>
      )}

      {open && idResult && (
        <div className="border-t px-4 py-3 bg-muted/30">
          <IdDiffDetail result={idResult} />
        </div>
      )}
    </div>
  )
}

const shortDate = (iso) => (iso ? iso.slice(0, 10) : null)

/**
 * Creation-time histogram for a set of keys. Every key is a Mongo ObjectId (or
 * embeds one), and an ObjectId carries its creation timestamp in its first four
 * bytes — so the shape of a gap is readable without any extra queries. A gap
 * that runs to today is a sync that has fallen behind; one that stops months ago
 * is an un-backfilled tail.
 */
function MonthBars({ timeline, tone }) {
  if (!timeline || timeline.months.length === 0) return null

  const months = timeline.months.slice(-12)
  const max = Math.max(...months.map(m => m.count))

  return (
    <div className="mt-2 space-y-1">
      <div className="flex items-center justify-between text-[11px] text-muted-foreground">
        <span>{shortDate(timeline.earliest)} → {shortDate(timeline.latest)}</span>
        {timeline.months.length > 12 && <span>last 12 mo of {timeline.months.length}</span>}
      </div>
      {months.map(m => (
        <div key={m.month} className="flex items-center gap-2 text-[11px]">
          <span className="font-mono text-muted-foreground w-14 shrink-0">{m.month}</span>
          <div className="flex-1 bg-muted rounded-sm h-2.5 overflow-hidden">
            <div
              className={`h-full rounded-sm ${tone === 'text-red-500' ? 'bg-red-500/60' : 'bg-amber-500/60'}`}
              style={{ width: `${max ? (m.count / max) * 100 : 0}%` }}
            />
          </div>
          <span className="font-mono w-12 text-right shrink-0">{m.count.toLocaleString()}</span>
        </div>
      ))}
      {timeline.undated > 0 && (
        <div className="text-[11px] text-muted-foreground pt-0.5">
          {num(timeline.undated)} key{timeline.undated === 1 ? '' : 's'} carry no ObjectId — not datable
        </div>
      )}
    </div>
  )
}

function KeyList({ title, tone, total, timeline, samples }) {
  if (!total) return null
  return (
    <div>
      <div className="text-xs font-medium mb-1">
        <span className={tone}>{title}</span>
        <span className="text-muted-foreground font-normal"> — {num(total)}</span>
      </div>

      <MonthBars timeline={timeline} tone={tone} />

      {samples.length > 0 && (
        <>
          <div className="text-[11px] text-muted-foreground mt-2 mb-1">
            Newest {samples.length}
          </div>
          <div className="text-xs bg-background border rounded p-2 max-h-48 overflow-auto space-y-0.5">
            {samples.map((s, i) => (
              <div key={`${s.key}-${i}`} className="flex items-baseline gap-2">
                <span className="font-mono truncate">{s.key || '(blank)'}</span>
                <span className="text-muted-foreground shrink-0 ml-auto text-[11px]">
                  {shortDate(s.created_at) || '—'}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

function IdDiffDetail({ result }) {
  if (result.error) {
    return <p className="text-sm text-destructive">{result.error}</p>
  }

  const stat = (label, value, tone) => (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`font-mono text-sm ${tone || ''}`}>{num(value)}</div>
    </div>
  )

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {stat('Matched', result.matched, 'text-green-600')}
        {stat('Missing in core', result.missing_in_core, result.missing_in_core ? 'text-red-500' : '')}
        {stat('Extra in core', result.extra_in_core, result.extra_in_core ? 'text-amber-600' : '')}
        <div>
          <div className="text-xs text-muted-foreground">Coverage</div>
          <div className="font-mono text-sm">
            {result.coverage_pct === null ? '—' : `${result.coverage_pct}%`}
          </div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Scan time</div>
          <div className="font-mono text-sm">{(result.duration_ms / 1000).toFixed(1)}s</div>
        </div>
      </div>

      {/* Rows vs distinct keys only diverge when a key repeats — show the
          arithmetic rather than quietly collapsing duplicates. */}
      {(result.legacy_duplicate_keys > 0 || result.core_duplicate_keys > 0 ||
        result.legacy_blank_keys > 0 || result.core_blank_keys > 0) && (
        <div className="text-xs text-muted-foreground grid grid-cols-2 sm:grid-cols-4 gap-2 border-t pt-2">
          <div>Legacy rows: <span className="font-mono">{num(result.legacy_rows)}</span> → {num(result.legacy_count)} distinct</div>
          <div>Core rows: <span className="font-mono">{num(result.core_rows)}</span> → {num(result.core_count)} distinct</div>
          <div>Duplicate keys: {num(result.legacy_duplicate_keys)} legacy / {num(result.core_duplicate_keys)} core</div>
          <div>Blank keys: {num(result.legacy_blank_keys)} legacy / {num(result.core_blank_keys)} core</div>
        </div>
      )}

      {/* Legacy records that could not be assigned to either clinic are excluded
          from the comparison above, so say how many rather than letting a clean
          match read as full coverage. */}
      {result.unattributed?.count > 0 && (
        <div className="text-xs border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30 rounded p-2 space-y-1">
          <div>
            <span className="font-medium text-amber-700 dark:text-amber-400">
              {num(result.unattributed.count)} unassigned
            </span>
            <span className="text-muted-foreground">
              {' '}of {num(result.legacy_total_all_targets)} legacy records — counted against neither clinic.
            </span>
          </div>
          <div className="text-muted-foreground">{result.unattributed.note}</div>
          {result.unattributed.samples?.length > 0 && (
            <div className="font-mono text-[11px] max-h-24 overflow-auto">
              {result.unattributed.samples.map((k, i) => (
                <div key={`${k}-${i}`} className="truncate">{k}</div>
              ))}
            </div>
          )}
        </div>
      )}

      {result.note && <p className="text-xs text-muted-foreground">{result.note}</p>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <KeyList
          title="Missing in core" tone="text-red-500"
          total={result.missing_in_core}
          timeline={result.timeline?.missing_in_core}
          samples={result.samples.missing_in_core}
        />
        <KeyList
          title="Extra in core" tone="text-amber-600"
          total={result.extra_in_core}
          timeline={result.timeline?.extra_in_core}
          samples={result.samples.extra_in_core}
        />
      </div>
    </div>
  )
}

function ConfigDiffTable({ data, filter, setFilter }) {
  const [expanded, setExpanded] = useState(new Set())

  const toggle = (key) => setExpanded(prev => {
    const next = new Set(prev)
    next.has(key) ? next.delete(key) : next.add(key)
    return next
  })

  const items = data.items.filter(i => filter === 'all' || i.status !== 'match')

  const STATUS_STYLE = {
    match: 'bg-green-500/10 text-green-600',
    drifted: 'bg-amber-500/10 text-amber-600',
    missing_in_core: 'bg-red-500/10 text-red-600',
    extra_in_core: 'bg-blue-500/10 text-blue-600',
  }
  const STATUS_LABEL = {
    match: 'Match',
    drifted: 'Drifted',
    missing_in_core: 'Missing in core',
    extra_in_core: 'Extra in core',
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        <Card><CardContent className="py-4 px-4">
          <span className="text-xs text-muted-foreground">Legacy / Core</span>
          <p className="text-2xl font-bold">{num(data.summary.legacy_count)} / {num(data.summary.core_count)}</p>
        </CardContent></Card>
        <Card><CardContent className="py-4 px-4">
          <span className="text-xs text-muted-foreground">Match</span>
          <p className="text-2xl font-bold text-green-600">{num(data.summary.match)}</p>
        </CardContent></Card>
        <Card><CardContent className="py-4 px-4">
          <span className="text-xs text-muted-foreground">Drifted</span>
          <p className={`text-2xl font-bold ${data.summary.drifted ? 'text-amber-600' : ''}`}>{num(data.summary.drifted)}</p>
        </CardContent></Card>
        <Card><CardContent className="py-4 px-4">
          <span className="text-xs text-muted-foreground">Missing in core</span>
          <p className={`text-2xl font-bold ${data.summary.missing_in_core ? 'text-red-500' : ''}`}>{num(data.summary.missing_in_core)}</p>
        </CardContent></Card>
        <Card><CardContent className="py-4 px-4">
          <span className="text-xs text-muted-foreground">Extra in core</span>
          <p className={`text-2xl font-bold ${data.summary.extra_in_core ? 'text-blue-600' : ''}`}>{num(data.summary.extra_in_core)}</p>
        </CardContent></Card>
      </div>

      <div className="flex items-center gap-2">
        <Button variant={filter === 'issues' ? 'default' : 'ghost'} size="sm" className="h-7 text-xs"
          onClick={() => setFilter('issues')}>Issues only</Button>
        <Button variant={filter === 'all' ? 'default' : 'ghost'} size="sm" className="h-7 text-xs"
          onClick={() => setFilter('all')}>All</Button>
        <span className="text-xs text-muted-foreground ml-2">
          {data.legacy_collection} → {data.core_collection}
        </span>
      </div>

      {items.length === 0 ? (
        <Card><CardContent className="py-8 text-center text-muted-foreground">
          <Check className="h-8 w-8 mx-auto mb-2 text-green-600" />
          <p>No differences — every record matches.</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {items.map(item => {
            const isOpen = expanded.has(item.key)
            return (
              <div key={item.key} className="border rounded-lg">
                <button
                  onClick={() => toggle(item.key)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-muted/50 transition-colors"
                >
                  {item.diffs.length > 0
                    ? (isOpen ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />)
                    : <span className="w-4 shrink-0" />}
                  <span className="font-medium flex-1 min-w-0 truncate">{item.label}</span>
                  {item.diffs.length > 0 && (
                    <span className="text-xs text-muted-foreground shrink-0">
                      {item.diffs.length} field{item.diffs.length === 1 ? '' : 's'}
                    </span>
                  )}
                  <span className={`px-2 py-0.5 rounded text-xs font-medium shrink-0 ${STATUS_STYLE[item.status]}`}>
                    {STATUS_LABEL[item.status]}
                  </span>
                </button>

                {isOpen && item.diffs.length > 0 && (
                  <div className="border-t px-4 py-3 bg-muted/30 space-y-2">
                    {item.diffs.map(d => (
                      <div key={d.field} className="grid grid-cols-1 sm:grid-cols-[160px_1fr_1fr] gap-2 text-sm">
                        <div className="font-mono text-xs text-muted-foreground pt-0.5">{d.field}</div>
                        <div className="text-xs break-words">
                          <span className="text-muted-foreground mr-1">legacy:</span>{fieldValue(d.legacy)}
                        </div>
                        <div className="text-xs break-words">
                          <span className="text-muted-foreground mr-1">core:</span>{fieldValue(d.core)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function QueuesTable({ data }) {
  const dq = data.summary.doctor_queue
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className={dq && !dq.in_sync ? 'border-red-500/40' : ''}>
          <CardContent className="py-4 px-4">
            <span className="text-xs text-muted-foreground">Doctor queue (ROUTING_COMPLETED)</span>
            <p className="text-2xl font-bold">
              {num(dq?.legacy)} <span className="text-muted-foreground text-base">→</span> {num(dq?.core)}
            </p>
            {dq && <p className={`text-xs ${dq.in_sync ? 'text-green-600' : 'text-red-500'}`}>{signed(dq.delta)}</p>}
          </CardContent>
        </Card>
        <Card><CardContent className="py-4 px-4">
          <span className="text-xs text-muted-foreground">Statuses drifted</span>
          <p className={`text-2xl font-bold ${data.summary.drifted ? 'text-amber-600' : 'text-green-600'}`}>
            {data.summary.drifted} <span className="text-muted-foreground text-base">/ {data.summary.statuses}</span>
          </p>
        </CardContent></Card>
        <Card><CardContent className="py-4 px-4">
          <span className="text-xs text-muted-foreground">Legacy orders</span>
          <p className="text-2xl font-bold">{num(data.summary.legacy_total)}</p>
        </CardContent></Card>
        <Card><CardContent className="py-4 px-4">
          <span className="text-xs text-muted-foreground">Core orders</span>
          <p className="text-2xl font-bold">{num(data.summary.core_total)}</p>
          <p className={`text-xs ${data.summary.delta === 0 ? 'text-green-600' : 'text-red-500'}`}>
            {signed(data.summary.delta)}
          </p>
        </CardContent></Card>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Status</th>
              <th className="text-right px-4 py-2 font-medium">Legacy</th>
              <th className="text-right px-4 py-2 font-medium">Core</th>
              <th className="text-right px-4 py-2 font-medium">Delta</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map(r => (
              <tr key={r.status} className={`border-t ${r.is_doctor_queue ? 'bg-primary/5' : ''}`}>
                <td className="px-4 py-2 font-mono text-xs">
                  {r.status} <OrderStatusInfo status={r.status} />
                  {r.is_doctor_queue && (
                    <span className="ml-2 px-1.5 py-0.5 rounded bg-primary/10 text-primary text-[10px] font-medium">
                      DOCTOR QUEUE
                    </span>
                  )}
                </td>
                <td className="px-4 py-2 text-right font-mono">{num(r.legacy)}</td>
                <td className="px-4 py-2 text-right font-mono">{num(r.core)}</td>
                <td className={`px-4 py-2 text-right font-mono ${r.in_sync ? 'text-green-600' : 'text-red-500'}`}>
                  {signed(r.delta)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/** Small titled stat for the routing view. */
function Stat({ label, value, tone, hint }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`font-mono text-lg font-semibold ${tone || ''}`}>{value}</div>
      {hint && <div className="text-[11px] text-muted-foreground">{hint}</div>}
    </div>
  )
}

function SampleList({ title, samples, render }) {
  if (!samples?.length) return null
  return (
    <div className="mt-2">
      <div className="text-[11px] text-muted-foreground mb-1">{title}</div>
      <div className="text-xs bg-background border rounded p-2 max-h-40 overflow-auto space-y-0.5 font-mono">
        {samples.map((s, i) => <div key={i} className="truncate">{render(s)}</div>)}
      </div>
    </div>
  )
}

/**
 * How Manifold is splitting live MDExam billing between WL and RRV: what it
 * sent core, whether its tag stores agree with the legacy tags, and whether
 * each payment landed in its patient's clinic.
 */
function RoutingView({ data }) {
  const m = data.manifold
  const tags = data.legacy_tags
  const cb = m?.chargebee_tags

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">How events are routed</CardTitle>
          <CardDescription>
            Manifold bills MDExam as one tenant and labels every delivery to core with the patient&apos;s clinic.
            Routing started {new Date(data.routing_start.at).toLocaleString()} ({data.routing_start.source}).
          </CardDescription>
        </CardHeader>
        <CardContent className="text-xs space-y-1">
          <div><span className="font-medium">Chargebee:</span> <span className="font-mono">{data.rules.chargebee}</span></div>
          <div><span className="font-medium">Hyperswitch:</span> <span className="font-mono">{data.rules.hyperswitch}</span></div>
          <div><span className="font-medium">Refunds:</span> {data.rules.refunds}</div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Legacy patient tags</CardTitle>
          <CardDescription>
            <span className="font-mono">customer_enhanced.clinic_tag</span>, which seeded Manifold&apos;s tag stores.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Stat label="Tagged WL" value={num(tags.counts.wl)} />
            <Stat label="Tagged RRV" value={num(tags.counts.rrv)} />
            <Stat label="Untagged" value={num(tags.counts.untagged)} tone={tags.counts.untagged ? 'text-amber-600' : ''} />
            <Stat
              label="Tag ≠ split date"
              value={num(tags.tag_vs_date_disagreements.count)}
              hint="The tag wins; listed for review"
            />
          </div>
          <SampleList
            title="Untagged patients"
            samples={tags.untagged_samples}
            render={s => `${s.key} · created ${shortDate(s.created_at) || '—'} · date rule says ${s.by_date || '—'}`}
          />
          <SampleList
            title="Tag disagrees with the split date"
            samples={tags.tag_vs_date_disagreements.samples}
            render={s => `${s.key} · created ${shortDate(s.created_at) || '—'} · tag ${s.tag} · date rule says ${s.by_date}`}
          />
        </CardContent>
      </Card>

      {!m?.configured ? (
        <Card><CardContent className="py-4 text-sm text-muted-foreground flex items-center gap-2">
          <Info className="h-4 w-4 shrink-0" /> {m?.note}
        </CardContent></Card>
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Deliveries to core since routing started</CardTitle>
              <CardDescription>Every MDEXAM event Manifold sent core, by the clinic it was labelled with.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <Stat label="Unlabelled / plain MDEXAM" value={num(m.unrouted_deliveries)} tone={m.unrouted_deliveries ? 'text-red-500' : 'text-green-600'} />
                <Stat label="Not succeeded" value={num(m.failed_deliveries)} tone={m.failed_deliveries ? 'text-red-500' : 'text-green-600'} />
              </div>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">Clinic</th>
                      <th className="text-left px-3 py-2 font-medium">Source</th>
                      <th className="text-left px-3 py-2 font-medium">Status</th>
                      <th className="text-right px-3 py-2 font-medium">Events</th>
                      <th className="text-right px-3 py-2 font-medium">Latest</th>
                    </tr>
                  </thead>
                  <tbody>
                    {m.deliveries.map((d, i) => (
                      <tr key={i} className="border-t">
                        <td className={`px-3 py-1.5 font-mono text-xs ${!d.clinic_name || d.clinic_name === 'MDEXAM' ? 'text-red-500' : ''}`}>{d.clinic_name || '(none)'}</td>
                        <td className="px-3 py-1.5 text-xs">{d.source}</td>
                        <td className={`px-3 py-1.5 text-xs ${d.status === 'succeeded' ? '' : 'text-red-500'}`}>{d.status}</td>
                        <td className="px-3 py-1.5 text-right font-mono">{num(d.count)}</td>
                        <td className="px-3 py-1.5 text-right text-xs text-muted-foreground">{d.last_at ? new Date(d.last_at).toLocaleString() : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Manifold Chargebee tags vs legacy</CardTitle>
              <CardDescription>
                <span className="font-mono">chargebee_clinic_tags</span> against each Chargebee customer&apos;s legacy tag.
                A customer missing from Manifold falls back to WL, so a missing RRV customer is misrouted live.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                <Stat label="Agree" value={num(cb.agree)} tone="text-green-600" hint={`of ${num(cb.legacy_resolved)} resolvable`} />
                <Stat label="Disagree" value={num(cb.disagree.count)} tone={cb.disagree.count ? 'text-red-500' : ''} />
                <Stat label="Missing in Manifold" value={num(cb.missing_in_manifold.count)} tone={cb.missing_in_manifold.count ? 'text-amber-600' : ''} hint="→ WL by fallback" />
                <Stat label="…of which RRV" value={num(cb.missing_in_manifold.misrouted_rrv.count)} tone={cb.missing_in_manifold.misrouted_rrv.count ? 'text-red-500' : 'text-green-600'} hint="misrouted to WL" />
                <Stat label="Only in Manifold" value={num(cb.extra_in_manifold.count)} />
              </div>
              <p className="text-[11px] text-muted-foreground mt-2">
                {num(cb.legacy_unresolved)} legacy Chargebee ids are not resolvable (shared by patients with different tags, or only untagged patients) and are not compared.
                Manifold holds {Object.entries(cb.manifold_by_tag).map(([t, n]) => `${num(n)} ${t}`).join(', ')}.
              </p>
              <SampleList title="Disagreements" samples={cb.disagree.samples} render={s => `${s.chargebee_id} · legacy ${s.legacy} · manifold ${s.manifold || '(none)'}`} />
              <SampleList title="RRV customers missing from Manifold" samples={cb.missing_in_manifold.misrouted_rrv.samples} render={s => s.chargebee_id} />
              <SampleList title="Only in Manifold" samples={cb.extra_in_manifold.samples} render={s => `${s.chargebee_id} · ${s.manifold}`} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Hyperswitch customers</CardTitle>
              <CardDescription>{m.hyperswitch_customers.note}</CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {Object.entries(m.hyperswitch_customers.by_tag).map(([tag, n]) => (
                <Stat key={tag} label={tag} value={num(n)} tone={tag === '(none)' && n ? 'text-amber-600' : ''} />
              ))}
            </CardContent>
          </Card>
        </>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Did each payment land in its patient&apos;s clinic?</CardTitle>
          <CardDescription>
            Core payments since routing started, checked against the patient&apos;s tag — Chargebee by the legacy
            Chargebee id, Hyperswitch by the Manifold customer. Unknown means the customer could not be resolved.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {data.landed.map(t => (
            <div key={t.id} className="border rounded-lg px-4 py-3">
              <div className="flex items-baseline gap-2 mb-2">
                <span className="font-medium">{t.label}</span>
                <code className="text-xs text-muted-foreground">{t.database}</code>
              </div>
              {Object.keys(t.by_kind).length === 0 ? (
                <p className="text-xs text-muted-foreground">No payments since routing started.</p>
              ) : (
                <div className="space-y-2">
                  {Object.entries(t.by_kind).map(([kind, b]) => (
                    <div key={kind}>
                      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
                        <div className="font-medium capitalize">{kind}</div>
                        <div><span className="text-xs text-muted-foreground">Payments </span><span className="font-mono">{num(b.total)}</span></div>
                        <div><span className="text-xs text-muted-foreground">Correct </span><span className="font-mono text-green-600">{num(b.correct)}</span></div>
                        <div><span className="text-xs text-muted-foreground">Wrong clinic </span><span className={`font-mono ${b.wrong.count ? 'text-red-500' : ''}`}>{num(b.wrong.count)}</span></div>
                        <div><span className="text-xs text-muted-foreground">Unknown </span><span className={`font-mono ${b.unknown ? 'text-amber-600' : ''}`}>{num(b.unknown)}</span></div>
                      </div>
                      <SampleList title="In the wrong clinic" samples={b.wrong.samples} render={s => `${s.transaction_id} · ${s.customer_id} · belongs to ${s.belongs_to}`} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">
        Generated {new Date(data.generated_at).toLocaleString()} in {(data.duration_ms / 1000).toFixed(1)}s
      </p>
    </div>
  )
}

export default function MigrationAuditPage() {
  const [activeTab, setActiveTab] = useState('overview')
  const [cache, setCache] = useState({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [filter, setFilter] = useState('issues')
  const [idResults, setIdResults] = useState({})
  const [idRunning, setIdRunning] = useState({})
  const [target, setTarget] = useState(TARGETS[0].id)

  // Each target is a different database, so a tab's result is only valid for the
  // target it was fetched under — cache and ID-diff results are keyed by both.
  const cacheKey = TARGET_SCOPED(activeTab) ? `${activeTab}:${target}` : activeTab

  const fetchTab = useCallback(async (tab, tgt, force = false) => {
    const key = TARGET_SCOPED(tab) ? `${tab}:${tgt}` : tab
    if (!force && cache[key]) return
    setLoading(true)
    setError(null)
    try {
      let response
      if (tab === 'overview') response = await adminAPI.getMigrationAudit()
      else if (tab === 'queues') response = await adminAPI.getMigrationQueues(tgt)
      else if (tab === 'routing') response = await adminAPI.getMigrationRouting()
      else response = await adminAPI.getMigrationConfigDiff(tab, tgt)

      if (response.data.success) {
        setCache(prev => ({ ...prev, [key]: response.data.data }))
      } else {
        setError(response.data.error?.message || 'Request failed')
      }
    } catch (err) {
      console.error('[MIGRATION-AUDIT] Error:', err)
      setError(err.response?.data?.error?.message || err.message)
    } finally {
      setLoading(false)
    }
  }, [cache])

  useEffect(() => { fetchTab(activeTab, target) }, [activeTab, target, fetchTab])

  const runIdDiff = useCallback(async (kind, tgt) => {
    const key = `${tgt}:${kind}`
    setIdRunning(prev => ({ ...prev, [key]: true }))
    try {
      const response = await adminAPI.getMigrationIdDiff(kind, 100, tgt)
      setIdResults(prev => ({
        ...prev,
        [key]: response.data.success
          ? response.data.data
          : { error: response.data.error?.message || 'Request failed' },
      }))
    } catch (err) {
      setIdResults(prev => ({
        ...prev,
        [key]: { error: err.response?.data?.error?.message || err.message },
      }))
    } finally {
      setIdRunning(prev => ({ ...prev, [key]: false }))
    }
  }, [])

  const data = cache[cacheKey]

  const refresh = () => {
    if (activeTab === 'overview') { setIdResults({}) }
    fetchTab(activeTab, target, true)
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">MDExam Migration Audit</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Legacy Rails <code className="text-xs">test</code> → core{' '}
            <code className="text-xs">MDEXAM_RRV</code> + <code className="text-xs">MDEXAM_WL</code> reconciliation
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={refresh} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          <span className="ml-1.5">Refresh</span>
        </Button>
      </div>

      {cache.overview?.sources && (
        <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <Database className="h-3 w-3" />
            legacy: {cache.overview.sources.legacy.cluster}/{cache.overview.sources.legacy.database}
          </span>
          {(cache.overview.targets || []).map(t => (
            <span key={t.id} className="flex items-center gap-1.5">
              <Database className="h-3 w-3" />
              {t.label}: {t.cluster}/{t.database}
            </span>
          ))}
        </div>
      )}

      {cache.overview?.split && (
        <div className="text-xs text-muted-foreground border rounded-lg px-3 py-2 bg-muted/30">
          Each patient goes to the clinic named by its{' '}
          <span className="font-mono">clinic_tag</span> — the same tag Manifold routes live billing by.
          Tags were seeded by the <span className="font-mono">{cache.overview.split.date}</span> split
          (created on or after → <span className="font-medium text-foreground">RRV</span>, earlier →{' '}
          <span className="font-medium text-foreground">WL</span>), but the tag decides. Orders and
          payments follow their patient.
          {cache.overview.split.legacy_untagged_patients > 0 && (
            <> {num(cache.overview.split.legacy_untagged_patients)} legacy patient(s) have no tag and are counted against neither clinic.</>
          )}
        </div>
      )}

      {TARGET_SCOPED(activeTab) && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Target clinic:</span>
          {TARGETS.map(t => (
            <Button
              key={t.id}
              variant={target === t.id ? 'default' : 'outline'}
              size="sm"
              className="h-7 text-xs"
              onClick={() => setTarget(t.id)}
            >
              {t.label}
            </Button>
          ))}
        </div>
      )}

      <div className="flex gap-1 border-b overflow-x-auto">
        {TABS.map(tab => (
          <button
            key={tab.value}
            onClick={() => { setActiveTab(tab.value); setFilter('issues') }}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === tab.value
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {error && (
        <Card><CardContent className="py-6 text-center">
          <AlertCircle className="h-8 w-8 text-destructive mx-auto mb-2" />
          <p className="text-destructive">{error}</p>
          <Button variant="outline" onClick={refresh} className="mt-3">Try Again</Button>
        </CardContent></Card>
      )}

      {loading && !data && (
        <Card><CardContent className="py-12 text-center text-muted-foreground">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
          <p>Comparing legacy against core…</p>
        </CardContent></Card>
      )}

      {!error && data && activeTab === 'overview' && (
        <>
          {(data.targets || []).map(t => (
            <div key={t.id} className="space-y-3">
              <div className="flex items-baseline gap-2 pt-2">
                <h2 className="text-lg font-semibold text-foreground">{t.label}</h2>
                <code className="text-xs text-muted-foreground">{t.database}</code>
                <span className="text-xs text-muted-foreground">— {t.blurb}</span>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Configuration</CardTitle>
                  <CardDescription>
                    Not patient-scoped — both clinics need the full legacy config, so each is
                    compared against the same legacy totals and must match 100% before cutover.
                    Use the Programs / Pharmacies / Physicians tabs for the field-level diff.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  {t.config.map(row => (
                    <CountRow
                      key={row.label} row={row}
                      idResult={idResults[`${t.id}:${row.kind}`]}
                      running={!!idRunning[`${t.id}:${row.kind}`]}
                      onRunIdDiff={(kind) => runIdDiff(kind, t.id)}
                    />
                  ))}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Records</CardTitle>
                  <CardDescription>
                    Legacy counts are this clinic&apos;s cohort only. Run the ID diff to see exactly
                    which records are missing — it scans every key on both clusters and takes up to
                    a minute.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  {t.volume.map(row => (
                    <CountRow
                      key={row.label} row={row}
                      idResult={idResults[`${t.id}:${row.kind}`]}
                      running={!!idRunning[`${t.id}:${row.kind}`]}
                      onRunIdDiff={(kind) => runIdDiff(kind, t.id)}
                    />
                  ))}
                </CardContent>
              </Card>
            </div>
          ))}

          <p className="text-xs text-muted-foreground">
            Generated {new Date(data.generated_at).toLocaleString()}
          </p>
        </>
      )}

      {/* keyed by tab so per-row expansion state resets when switching entity */}
      {!error && data && CONFIG_TABS.has(activeTab) && (
        <ConfigDiffTable key={activeTab} data={data} filter={filter} setFilter={setFilter} />
      )}

      {!error && data && activeTab === 'queues' && <QueuesTable data={data} />}

      {!error && data && activeTab === 'routing' && <RoutingView data={data} />}
    </div>
  )
}
